/**
 * Your own money accounts (treasurer only, not part of any group). Today these are credit
 * cards: statement cycle, due date, limit, and available credit worked out from the balance you
 * last entered plus what you've logged on the card in your groups since. The same shape holds
 * bank accounts later (kind, and an external id for bank sync), but those aren't offered yet.
 */
import type { GroupState, IsoDate } from "../types.ts";
import { computeLedger } from "../engine/ledger.ts";
import type { Micro } from "../money.ts";
import { cycleBeforeDue, type CardDetails } from "./cardDetails.ts";

export type AccountKind = "credit" | "checking" | "savings";

export interface MoneyAccount {
  id: string;
  kind: AccountKind;
  name: string;
  last4: string;
  issuer: string;
  /** Credit limit, in cents (credit cards). */
  limitCents: number | null;
  /** The balance as of `balanceOn`: what the card's app showed when you entered it. */
  balanceCents: number;
  balanceOn: IsoDate;
  /** Day of the month the statement closes (1–31; short months use their last day). */
  statementDay: number | null;
  /** Day of the month the payment is due. */
  dueDay: number | null;
  /** Names this card goes by in the ledgers' Card column, like "Amex Gold" or "Chase 1234". */
  matchNames: string[];
  /** For bank sync later (for example a Plaid account id). Unused for now. */
  externalId: string | null;
  archived: boolean;
  /** Your notes ("Fidelity CMA, core position FDLXX"), which the AI suggestions read too. */
  note?: string;
  /** Earn rates by merchant type (credit cards). */
  rewards?: import("./rewards.ts").CardRewards | null;
  /** Network, tier, charge or business, how the statement closes, welcome bonus, points program. */
  details?: CardDetails | null;
}

/** A balance you entered or a payment you made, kept for charts. */
export interface MoneyEntry {
  id: string;
  accountId: string;
  kind: "balance" | "payment";
  on: IsoDate;
  cents: number;
  note: string;
}

export interface CardPurchase {
  groupId: string;
  groupName: string;
  itemId: string;
  date: IsoDate;
  store: string;
  name: string;
  /** The whole amount charged, tax and tip included. */
  totalMicro: Micro;
}

export interface CardStatus {
  /** Balance plus what you've logged since then, in micro. */
  owedMicro: Micro;
  /** Logged on the card after the balance date. */
  loggedSinceMicro: Micro;
  loggedSince: CardPurchase[];
  availableMicro: Micro | null;
  /** How much of the limit is used, in basis points (3000 = 30%). */
  utilizationBp: number | null;
  /** Spending logged in the current statement cycle. */
  cycleMicro: Micro;
  lastClose: IsoDate | null;
  nextClose: IsoDate | null;
  /** The next payment due date. */
  nextDue: IsoDate | null;
  daysToClose: number | null;
  daysToDue: number | null;
}

/** How a card shows in a Card column: "Amex Blue Business Plus" (the issuer first, unless the name has it). */
export function cardLabel(a: { name: string; issuer?: string }): string {
  const issuer = (a.issuer ?? "").trim();
  if (!issuer || a.name.toLowerCase().includes(issuer.toLowerCase())) return a.name;
  return `${issuer} ${a.name}`;
}

/** The given day in a month, clamped to the month's length (31 in February is the 28th or 29th). */
export function dayIn(year: number, month0: number, day: number): IsoDate {
  const last = new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate();
  return new Date(Date.UTC(year, month0, Math.min(day, last))).toISOString().slice(0, 10);
}

function parts(iso: IsoDate) {
  return { y: Number(iso.slice(0, 4)), m: Number(iso.slice(5, 7)) - 1 };
}

function days(a: IsoDate, b: IsoDate): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}

/** The statement close on or before today, and the next one after today. */
export function statementCycle(
  day: number,
  today: IsoDate,
): { lastClose: IsoDate; nextClose: IsoDate } {
  const { y, m } = parts(today);
  const thisMonth = dayIn(y, m, day);
  if (thisMonth <= today) return { lastClose: thisMonth, nextClose: dayIn(y, m + 1, day) };
  return { lastClose: dayIn(y, m - 1, day), nextClose: thisMonth };
}

/** The next due date on or after today. */
export function nextDueDate(day: number, today: IsoDate): IsoDate {
  const { y, m } = parts(today);
  const thisMonth = dayIn(y, m, day);
  return thisMonth >= today ? thisMonth : dayIn(y, m + 1, day);
}

const norm = (s: string) => s.trim().toLowerCase();

/** Every item paid with this card (by its name or the names it goes by), across groups. */
export function cardPurchases(
  account: Pick<MoneyAccount, "name" | "matchNames"> & { issuer?: string },
  groups: readonly { id: string; name: string; state: GroupState }[],
  today: IsoDate,
): CardPurchase[] {
  const names = new Set(
    [account.name, cardLabel(account), ...account.matchNames].map(norm).filter(Boolean),
  );
  const out: CardPurchase[] = [];
  for (const g of groups) {
    const ledger = computeLedger(g.state, today);
    for (const c of ledger.items) {
      const paid = c.item.paidWith;
      if (!paid || !names.has(norm(paid))) continue;
      out.push({
        groupId: g.id,
        groupName: g.name,
        itemId: c.item.id,
        date: c.item.date,
        store: c.item.store,
        name: c.item.name,
        totalMicro: c.totalMicro,
      });
    }
  }
  return out.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}

export function cardStatus(
  account: MoneyAccount,
  purchases: readonly CardPurchase[],
  today: IsoDate,
): CardStatus {
  // Purchases on the balance date may already be in it, so only later ones count.
  const loggedSince = purchases.filter((p) => p.date > account.balanceOn && p.date <= today);
  const loggedSinceMicro = loggedSince.reduce((s, p) => s + p.totalMicro, 0);
  const owedMicro = account.balanceCents * 10_000 + loggedSinceMicro;
  // Charge cards have no preset limit, so there's nothing "available" to show.
  const charge = !!account.details?.charge;
  const limit = account.limitCents === null || charge ? null : account.limitCents * 10_000;
  const mode = account.details?.cycle;
  const cycle =
    mode?.mode === "before-due" && account.dueDay
      ? cycleBeforeDue(account.dueDay, mode.days, today)
      : account.statementDay
        ? statementCycle(account.statementDay, today)
        : null;
  const nextDue = account.dueDay ? nextDueDate(account.dueDay, today) : null;
  return {
    owedMicro,
    loggedSinceMicro,
    loggedSince,
    availableMicro: limit === null ? null : limit - owedMicro,
    utilizationBp: limit ? Math.round((Math.max(0, owedMicro) / limit) * 10_000) : null,
    cycleMicro: cycle
      ? purchases
          .filter((p) => p.date > cycle.lastClose && p.date <= today)
          .reduce((s, p) => s + p.totalMicro, 0)
      : 0,
    lastClose: cycle?.lastClose ?? null,
    nextClose: cycle?.nextClose ?? null,
    nextDue,
    daysToClose: cycle ? days(today, cycle.nextClose) : null,
    daysToDue: nextDue ? days(today, nextDue) : null,
  };
}

/** All credit cards together: total limit, owed and available. */
export function creditTotals(statuses: readonly { account: MoneyAccount; status: CardStatus }[]) {
  let limitMicro = 0;
  let owedMicro = 0;
  /** Owed on cards with a limit (charge cards don't count toward how much of your limit is used). */
  let revolvingMicro = 0;
  for (const { account, status } of statuses) {
    if (account.kind !== "credit" || account.archived) continue;
    owedMicro += status.owedMicro;
    if (account.details?.charge) continue;
    limitMicro += (account.limitCents ?? 0) * 10_000;
    revolvingMicro += status.owedMicro;
  }
  return {
    limitMicro,
    owedMicro,
    availableMicro: limitMicro - revolvingMicro,
    utilizationBp: limitMicro
      ? Math.round((Math.max(0, revolvingMicro) / limitMicro) * 10_000)
      : null,
  };
}

/**
 * The card's balance on each date, as best known: the latest balance you entered on or before
 * that day, plus purchases logged after it and minus payments you logged after it.
 */
export function balanceSeries(
  account: MoneyAccount,
  entries: readonly MoneyEntry[],
  purchases: readonly CardPurchase[],
  dates: readonly IsoDate[],
): { date: IsoDate; valueMicro: Micro }[] {
  const mine = entries.filter((e) => e.accountId === account.id);
  const balances = [
    ...mine.filter((e) => e.kind === "balance").map((e) => ({ on: e.on, cents: e.cents })),
    { on: account.balanceOn, cents: account.balanceCents },
  ].sort((a, b) => (a.on < b.on ? -1 : a.on > b.on ? 1 : 0));
  const payments = mine.filter((e) => e.kind === "payment");
  return dates.map((date) => {
    let base: { on: IsoDate; cents: number } | undefined;
    for (const b of balances) if (b.on <= date) base = b;
    if (!base) {
      // Before any balance you entered: count purchases from the start.
      const spent = purchases.filter((p) => p.date <= date).reduce((s, p) => s + p.totalMicro, 0);
      const paid = payments.filter((p) => p.on <= date).reduce((s, p) => s + p.cents, 0);
      return { date, valueMicro: spent - paid * 10_000 };
    }
    const from = base.on;
    const spent = purchases
      .filter((p) => p.date > from && p.date <= date)
      .reduce((s, p) => s + p.totalMicro, 0);
    const paid = payments
      .filter((p) => p.on > from && p.on <= date)
      .reduce((s, p) => s + p.cents, 0);
    return { date, valueMicro: base.cents * 10_000 + spent - paid * 10_000 };
  });
}
